import { useEffect, useState } from 'react';

/** Maps ISO 3166-1 alpha-2 country code → local currency symbol. */
const COUNTRY_TO_SYMBOL: Record<string, string> = {
  // Pound sterling
  GB: '£',
  // Euro
  AD: '€', AT: '€', BE: '€', CY: '€', DE: '€', EE: '€', ES: '€',
  FI: '€', FR: '€', GR: '€', HR: '€', IE: '€', IT: '€', LT: '€',
  LU: '€', LV: '€', MC: '€', ME: '€', MT: '€', NL: '€', PT: '€',
  SI: '€', SK: '€', SM: '€', VA: '€', XK: '€',
  // Australian / NZ dollar
  AU: 'A$', NZ: 'NZ$',
  // Singapore / HK dollar
  SG: 'S$', HK: 'HK$',
  // Canadian dollar
  CA: 'CA$',
  // South African rand
  ZA: 'R',
  // Indian rupee
  IN: '₹',
  // Brazilian real
  BR: 'R$',
  // Mexican peso
  MX: 'MX$',
  // Japanese yen
  JP: '¥',
  // South Korean won
  KR: '₩',
  // Swiss franc
  CH: 'CHF',
  // Norwegian / Swedish / Danish krone
  NO: 'kr', SE: 'kr', DK: 'kr',
  // Israeli shekel
  IL: '₪',
  // UAE dirham
  AE: 'AED',
  // Saudi riyal
  SA: 'SAR',
};

const SESSION_KEY = 'kc_currency_symbol';

/**
 * Returns a currency symbol based on the visitor's IP geolocation.
 * Falls back to `$` on error or timeout.
 * Result is cached in sessionStorage for the page session.
 */
export function usePricingSymbol(): string {
  const [symbol, setSymbol] = useState<string>(() => {
    return sessionStorage.getItem(SESSION_KEY) ?? '$';
  });

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return; // already resolved

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then(r => r.json())
      .then((data: { country_code?: string }) => {
        const s = (data.country_code && COUNTRY_TO_SYMBOL[data.country_code]) ?? '$';
        sessionStorage.setItem(SESSION_KEY, s);
        setSymbol(s);
      })
      .catch(() => {
        // Silently fall back to $
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, []);

  return symbol;
}
